import React from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiFileText, FiLayers } from "react-icons/fi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CrudPage } from "../components/CrudPage";
import { PaymentBatchCardForm } from "../components/forms/PaymentBatchCardForm";
import {
  PaymentStatusBadge,
  formatPaymentCardAmount,
} from "../components/common/PaymentCardVisual";
import { InvestmentPaymentCard } from "../components/common/InvestmentPaymentCard";
import { paymentBatchCards, paymentCards } from "../services/api";
import { useAuth } from "../context/AuthContext";

const pageSize = 20;

const formatRial = (value?: number) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const formatCardNumber = (value?: string) =>
  String(value || "")
    .replace(/[^\d]/g, "")
    .replace(/(\d{4})(?=\d)/g, "$1-");

const statusMeta: Record<string, { label: string; color: string }> = {
  active: { label: "فعال", color: "green" },
  used: { label: "استفاده شده", color: "blue" },
  cancelled: { label: "لغو شده", color: "red" },
};

export const PaymentCardsPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [selectedItem, setSelectedItem] = React.useState<any>(null);
  const [itemToDelete, setItemToDelete] = React.useState<any>(null);
  const createModal = useDisclosure();
  const detailsModal = useDisclosure();
  const deleteModal = useDisclosure();
  const pageBg = useColorModeValue("white", "dark.50");
  const subtleBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const createMutation = useMutation(paymentBatchCards.create, {
    onSuccess: (response: any) => {
      queryClient.invalidateQueries(["paymentCards"]);
      queryClient.invalidateQueries(["paymentBatchCards"]);
      createModal.onClose();
      toast({
        title: response?.message || "عملیات با موفقیت انجام شد",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در ایجاد کارت یا بسته کارت",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    },
  });

  const deleteMutation = useMutation(paymentCards.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries(["paymentCards"]);
      queryClient.invalidateQueries(["paymentBatchCards"]);
      deleteModal.onClose();
      toast({
        title: "کارت با موفقیت لغو شد",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: "خطا در لغو کارت",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    },
  });

  const handleCopyCardNumber = async (cardNumber?: string) => {
    const normalizedCardNumber = String(cardNumber || "").replace(/[^\d]/g, "");

    if (!normalizedCardNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(normalizedCardNumber);
      toast({
        title: "شماره کارت کپی شد",
        description: formatCardNumber(normalizedCardNumber),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "امکان کپی شماره کارت وجود ندارد",
        description: error?.message || "خطای ناشناخته",
        status: "error",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  const columns = [
    {
      header: "شماره کارت",
      accessor: "cardNumber",
      render: (item: any) => (
        <Center justifyContent="flex-start">
          <Text
            as="button"
            type="button"
            fontWeight="semibold"
            textDecoration="underline"
            textUnderlineOffset="3px"
            cursor="copy"
            onClick={(event) => {
              event.stopPropagation();
              handleCopyCardNumber(item.cardNumber);
            }}
          >
            {formatCardNumber(item.cardNumber)}
          </Text>
        </Center>
      ),
    },
    {
      header: "عنوان بسته",
      accessor: "batchId",
      render: (item: any) => item.batchId?.title || "—",
    },
    {
      header: "مبلغ",
      accessor: "amount",
      render: (item: any) => formatRial(item.amount),
    },
    {
      header: "صندوق",
      accessor: "fundName",
      render: (item: any) => item.fundName || "—",
    },
    {
      header: "وضعیت",
      accessor: "status",
      render: (item: any) => {
        const meta = statusMeta[item.status] || {
          label: item.status || "—",
          color: "gray",
        };
        return <Badge colorScheme={meta.color}>{meta.label}</Badge>;
      },
    },
    {
      header: "انقضا",
      accessor: "expiresAt",
      render: (item: any) => formatDate(item.expiresAt),
    },
    {
      header: "استفاده در",
      accessor: "usedAt",
      render: (item: any) => formatDate(item.usedAt),
    },
  ];

  const handleView = async (item: any) => {
    try {
      const fullItem = await paymentCards.getOne(item._id);
      setSelectedItem(fullItem);
      detailsModal.onOpen();
    } catch (error: any) {
      toast({
        title: "خطا در دریافت جزئیات",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <CrudPage
        title="کارت‌های پرداخت"
        columns={columns}
        queryKey="paymentCards"
        api={paymentCards}
        FormComponent={() => null}
        resourceName="paymentCards"
        enableView
        enableEdit={false}
        enableDelete
        pageSize={pageSize}
        keepPreviousData
        customAddAction={
          hasPermission("paymentCards:create")
            ? () => createModal.onOpen()
            : undefined
        }
        customViewAction={handleView}
        customDeleteAction={
          hasPermission("paymentCards:delete")
            ? (item) => {
                setItemToDelete(item);
                deleteModal.onOpen();
              }
            : undefined
        }
      />

      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ایجاد کارت یا بسته کارت هدیه</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody py={4}>
            <PaymentBatchCardForm
              onSubmit={(formData) => createMutation.mutate(formData)}
              onCancel={createModal.onClose}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={detailsModal.isOpen}
        onClose={detailsModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>جزئیات کارت هدیه</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={5}>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="3xl"
                overflow="hidden"
                bg={pageBg}
              >
                <Box px={{ base: 3, md: 4 }} py={{ base: 3, md: 4 }}>
                  <InvestmentPaymentCard
                    cardNumber={selectedItem?.cardNumber}
                    amount={selectedItem?.amount}
                    expiresInfo={{
                      label: "انقضا",
                      value: selectedItem?.expiresAt,
                    }}
                    status={selectedItem?.status}
                    usedAt={selectedItem?.usedAt}
                    cancelledAt={
                      selectedItem?.cancelledAt ||
                      selectedItem?.batchId?.cancelledAt
                    }
                    fundName={
                      selectedItem?.fundName ||
                      selectedItem?.request?.fundName ||
                      "صندوق سرمایه‌گذاری"
                    }
                  />
                </Box>
              </Box>

              <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={5}>
                <RecordPanel
                  title="درخواست مرتبط"
                  icon={FiFileText}
                  bg={subtleBg}
                  borderColor={borderColor}
                  rows={[
                    {
                      label: "شماره رهگیری",
                      value: selectedItem?.request?.trackId,
                    },
                    {
                      label: "وضعیت",
                      value:
                        statusMeta[selectedItem?.request?.status || ""]
                          ?.label ||
                        selectedItem?.request?.status ||
                        "—",
                    },
                    {
                      label: "روش پرداخت",
                      value:
                        selectedItem?.request?.paymentMethod === "card"
                          ? "کارت هدیه"
                          : selectedItem?.request?.paymentMethod === "gateway"
                            ? "درگاه پرداخت"
                            : "—",
                    },
                    {
                      label: "صندوق",
                      value:
                        selectedItem?.fundName ||
                        selectedItem?.request?.fundName ||
                        "—",
                    },
                    {
                      label: "مبلغ",
                      value: formatRial(selectedItem?.request?.amountRial),
                    },
                    {
                      label: "تعداد واحد",
                      value: selectedItem?.request?.units
                        ? Number(
                            selectedItem.request.units / 10000,
                          ).toLocaleString("fa-IR")
                        : "—",
                    },
                    {
                      label: "شماره مرجع بانکی",
                      value: selectedItem?.request?.paymentGateway?.rrn || "—",
                    },
                    {
                      label: "نام سرمایه‌گذار",
                      value: selectedItem?.request?.investor?.fullName || "—",
                    },
                    {
                      label: "کدملی سرمایه گذار",
                      value: selectedItem?.request?.investor?.nationalId || "—",
                    },
                    {
                      label: "تاریخ ثبت",
                      value: formatDate(selectedItem?.request?.createdAt),
                    },
                  ]}
                />

                <RecordPanel
                  title="بسته کارت"
                  icon={FiLayers}
                  bg={subtleBg}
                  borderColor={borderColor}
                  rows={[
                    {
                      label: "عنوان",
                      value: selectedItem?.batchId?.title || "—",
                    },
                    {
                      label: "وضعیت",
                      value:
                        selectedItem?.status === "used" ? (
                          <PaymentStatusBadge
                            label="استفاده شده"
                            colorScheme="blue"
                          />
                        ) : (
                          statusMeta[selectedItem?.status || ""]?.label ||
                          selectedItem?.batchId?.status ||
                          "—"
                        ),
                    },
                    {
                      label: "نوع ایجاد",
                      value:
                        selectedItem?.batchId?.createMode === "single"
                          ? "تکی"
                          : "بسته‌ای",
                    },
                    {
                      label: "تعداد کارت",
                      value: selectedItem?.batchId?.quantity
                        ? Number(selectedItem.batchId.quantity).toLocaleString(
                            "fa-IR",
                          )
                        : "—",
                    },
                    {
                      label: "صندوق",
                      value: selectedItem?.fundName || "—",
                    },
                    {
                      label: "مبلغ هر کارت",
                      value: formatRial(selectedItem?.batchId?.amount),
                    },
                    {
                      label: "مبلغ کل",
                      value: formatRial(selectedItem?.batchId?.totalAmount),
                    },
                    {
                      label: "مبلغ کارت",
                      value: formatPaymentCardAmount(selectedItem?.amount),
                    },
                    {
                      label: "تاریخ انقضا",
                      value: formatDate(selectedItem?.batchId?.expiresAt),
                    },
                    {
                      label: "توضیحات",
                      value: selectedItem?.batchId?.description || "—",
                    },
                  ]}
                />
              </Grid>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>لغو کارت</ModalHeader>
          <ModalCloseButton left={4} right="auto" aria-label="بستن" />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <div>این عملیات فقط برای کارت‌های فعال انجام می‌شود.</div>
              <Button
                colorScheme="red"
                onClick={() =>
                  itemToDelete?._id && deleteMutation.mutate(itemToDelete._id)
                }
                isLoading={deleteMutation.isLoading}
              >
                تایید لغو کارت
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const RecordPanel = ({
  title,
  icon,
  rows,
  bg,
  borderColor,
}: {
  title: string;
  icon: any;
  rows: Array<{ label: string; value: any }>;
  bg: string;
  borderColor: string;
}) => {
  const pageBg = useColorModeValue("white", "dark.50");
  const iconBg = useColorModeValue("brand.100", "icon.bg");
  const iconColor = useColorModeValue("brand.500", "icon.svg");

  return (
    <Box
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="3xl"
      overflow="hidden"
      bg={pageBg}
    >
      <HStack px={5} py={4} spacing={3} bg={bg}>
        <Box p={2} borderRadius="xl" bg={iconBg} color={iconColor}>
          <Icon as={icon} boxSize={5} verticalAlign={"middle"} />
        </Box>
        <Text fontWeight="bold">{title}</Text>
      </HStack>
      <Divider />
      <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4} p={5}>
        {rows.map((row) => (
          <GridItem
            key={`${title}-${row.label}`}
            colSpan={{ base: 2, md: row.label === "توضیحات" ? 2 : 1 }}
          >
            <Text fontSize="xs" color="gray.500" mb={1.5}>
              {row.label}
            </Text>
            {React.isValidElement(row.value) ? (
              row.value
            ) : (
              <Text
                fontWeight="semibold"
                color="gray.800"
                _dark={{ color: "white" }}
              >
                {row.value || "—"}
              </Text>
            )}
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};
